import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  useTheme,
  Skeleton,
} from '@mui/material';
import ButtonMAIQ from 'app/components/ReusableUIComponents/LowLevelComponents/buttons/ButtonMAIQ';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import ConfirmationModal from 'app/components/ConfirmationModal';
import DataGridMAIQ from 'app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ';
import DataGridTypes from 'types/DataGridTypes';
import {
  useValueContent,
  ValueContentProvider,
} from 'app/context/ValueContentContext';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import AddIcon from '@mui/icons-material/Add';
import { ValueContent } from 'app/apiService/valueContent/valueContentApiService';
import DealerValueDetailsModal from './DealerValueDetailsModal';
import AddValueContent from './AddValueContent';

interface ValueContentTabProps {}

const ValueContentTabContent: React.FC<ValueContentTabProps> = () => {
  const theme = useTheme();
  const location = useLocation();
  const selectedDealer = useSelector(selectDealerSelected);
  
  // Check if we're in NewAIAvatarLayout context
  const isInAIAvatarLayout = location.pathname.startsWith('/ai-avatar-layout');
  const {
    loading,
    error,
    valueContent,
    selectedContent,
    selectedDealerId,
    selectedDealerName,
    addValueContent,
    deleteValueContent,
    editValueContent,
    getValueContent,
    refetchValueContent,
    enableQuery,
  } = useValueContent();

  const [refreshKey, setRefreshKey] = useState(0);

  // Delete confirmation modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteContentId, setDeleteContentId] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState(false);

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedDealerValue, setSelectedDealerValue] =
    useState<ValueContent | null>(null);
  const [isEditingDetailModal, setIsEditingDetailModal] = useState(false);

  // Add/Edit modal state
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editingContent, setEditingContent] = useState<ValueContent | null>(null);

  // Get selected dealer ID
  const getSelectedDealerId = () => {
    if (selectedDealer === 'default') {
      return 'default';
    }
    return selectedDealer?.dealer || 'default';
  };

  // Check if dealer is selected - if not, show message
  if (!selectedDealerId) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
          color: 'text.primary',
          fontSize: '1.5rem',
          fontWeight: 500,
        }}
      >
        <Typography variant="h4" gutterBottom component="div">
          Please select a dealer to view value content
        </Typography>
      </Box>
    );
  }

  // Enable query when component mounts
  useEffect(() => {
    console.log('Enabling query, selectedDealer:', selectedDealer);
    enableQuery();
  }, [enableQuery]);

  // Debug valueContent
  useEffect(() => {
    console.log('valueContent updated:', valueContent);
    console.log('loading:', loading);
    console.log('error:', error);
  }, [valueContent, loading, error]);

  // Debug modal state
  useEffect(() => {
    console.log('Modal state changed:', {
      detailModalOpen,
      selectedDealerValue,
      isEditingDetailModal,
    });
  }, [detailModalOpen, selectedDealerValue, isEditingDetailModal]);

  const handleAddNew = () => {
    setEditingContent(null);
    setAddModalOpen(true);
  };

  const handleEdit = (content: ValueContent) => {
    setEditingContent(content);
    setAddModalOpen(true);
  };

  const handleSave = async (data: Partial<ValueContent>) => {
    try {
      if (editingContent) {
        // Edit existing content
        await editValueContent(editingContent._id, {
          ...data,
          dealership_id: getSelectedDealerId(),
        });
      } else {
        // Add new content
        await addValueContent({
          ...data,
          dealership_id: getSelectedDealerId(),
        });
      }

      setAddModalOpen(false);
      setEditingContent(null);
      setRefreshKey(prev => prev + 1);
      refetchValueContent();
    } catch (error) {
      console.error('Error saving value content:', error);
      throw error; // Re-throw to let the AddValueContent component handle it
    }
  };

  const handleCellClick = (cell: any, clickedElement?: string) => {
    if (cell.field === 'actions') {
      // Handle action buttons (edit, delete)
      if (clickedElement === 'delete') {
        const contentId = cell.row._id || cell.row.id || cell.id;
        handleDelete(contentId);
      } else if (clickedElement === 'edit') {
        handleEdit(cell.row);
      }
    } else if (cell.field === 'headline') {
      // Handle headline click - open detail modal in view mode
      setSelectedDealerValue(cell.row);
      setDetailModalOpen(true);
      setIsEditingDetailModal(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeleteContentId(id);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await deleteValueContent(deleteContentId);
      setDeleteModalOpen(false);
      setRefreshKey(prev => prev + 1);
      refetchValueContent();
    } catch (error) {
      console.error('Error deleting value content:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setDeleteContentId('');
  };

  // DataGrid configuration
  const dataGridConfig = useMemo(() => {
    return DataGridTypes.VALUE_CONTENT(getSelectedDealerId());
  }, [getSelectedDealerId]);

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
              Value Content Settings
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.primary' }}>
              Manage your value proposition content for{' '}
              {selectedDealerName || 'selected dealer'}
            </Typography>
          </Box>
          <ButtonMAIQ
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            sx={isInAIAvatarLayout ? {
              backgroundColor: theme.palette.mode === 'light' ? theme.palette.common.black : theme.palette.common.white,
              color: theme.palette.mode === 'light' ? theme.palette.common.white : theme.palette.common.black,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[800] : theme.palette.grey[200],
              },
            } : undefined}
          >
            Add New
          </ButtonMAIQ>
        </Box>

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <DataGridMAIQ
          type={dataGridConfig}
          onCellClick={handleCellClick}
          key={refreshKey}
        />

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          open={deleteModalOpen}
          title="Delete Value Content"
          message="Are you sure you want to delete this value content? This action cannot be undone."
          onConfirm={handleDeleteConfirm}
          onClose={handleDeleteCancel}
        />

        {/* Add/Edit Value Content Modal */}
        <AddValueContent
          open={addModalOpen}
          onClose={() => {
            setAddModalOpen(false);
            setEditingContent(null);
          }}
          onSave={handleSave}
          initialData={editingContent}
          isEditing={!!editingContent}
        />
      </Paper>

      {/* Detail Modal */}
      {selectedDealerValue && (
        <>
          {console.log('Rendering DealerValueDetailsModal with:', {
            open: detailModalOpen,
            data: selectedDealerValue,
            isEditing: isEditingDetailModal,
          })}
          <DealerValueDetailsModal
            open={detailModalOpen}
            onClose={() => setDetailModalOpen(false)}
            data={selectedDealerValue}
            onEdit={() => setIsEditingDetailModal(true)}
            onDelete={async () => {
              await deleteValueContent(selectedDealerValue._id);
              setDetailModalOpen(false);
              setRefreshKey(prev => prev + 1);
              refetchValueContent();
            }}
            onSave={async updated => {
              await editValueContent(selectedDealerValue._id, updated);
              setIsEditingDetailModal(false);
              setRefreshKey(prev => prev + 1);
              refetchValueContent();
            }}
            isEditing={isEditingDetailModal}
            setIsEditing={setIsEditingDetailModal}
            setRefreshKey={setRefreshKey}
          />
        </>
      )}
    </Box>
  );
};

const ValueContentTab: React.FC<ValueContentTabProps> = () => {
  return (
    <ValueContentProvider>
      <ValueContentTabContent />
    </ValueContentProvider>
  );
};

export default ValueContentTab;
